import { useSkillTemplates } from "@/api/skill-templates";
import { useSkills } from "@/api/skills";
import { useUserInfo } from "@/api/user";
import { ActionRow } from "@/components/rail-kit/ActionRow";
import { Accordion } from "@/components/ui/accordion";
import { RailFavoritesDropTarget } from "@/dnd";
import { RailGroup } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailGroup";
import { RailMultiSelectionMenuProvider } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailMultiSelectionMenu";
import {
  RailRowMenu,
  railRowMenuProps,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailRowMenu";
import { RailSelectionBar } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionBar";
import { RAIL_SELECTION_ROOT_ATTR } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import { RailZeroState } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailZeroState";
import { railZeroStateCopy } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/railZeroStateCopy";
import { useRailRowContextMenu } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/useRailRowContextMenu";
import {
  addSkillMenuItems,
  SkillsRailAddSkillMenu,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/skills/SkillsRailAddSkillMenu";
import { SkillsRailDraggableSkillRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/skills/SkillsRailDraggableSkillRow";
import { TemplatesRailRow } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/skills/TemplatesRailRow";
import { useSkillsRailAddActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/skills/useSkillsRailAddActions";
import { useSkillsRailMultiSelectionActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/skills/useSkillsRailMultiSelectionActions";
import { useActiveSkillTemplateId } from "@/panes/skill-template/useActiveSkillTemplateId";
import { useActiveSkillId } from "@/panes/skill/useActiveSkillId";
import { RESOURCE_TAG_FAVORITE } from "@cohesive/api-client";
import { LayoutTemplate, Plus, ScrollText, Star, User } from "lucide-react";

export function SkillsRail() {
  const { data: skills = [], isError } = useSkills();
  const { data: templates = [] } = useSkillTemplates();
  const { data: me } = useUserInfo();
  const activeSkillId = useActiveSkillId();
  const activeTemplateId = useActiveSkillTemplateId();
  const addSkillMenu = useRailRowContextMenu();
  const { createFromScratch, openUploadZipDialog, uploadZipDialog } =
    useSkillsRailAddActions();
  const {
    selectionCount,
    selectionBarActions,
    leftRailMultiActions,
    clear,
    clearIfOutside,
  } = useSkillsRailMultiSelectionActions();

  if (isError) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center px-4 py-8">
        <p className="text-center text-sm text-n-40">
          Couldn&apos;t load skills.
        </p>
      </div>
    );
  }

  const favoriteRows = skills
    .filter((skill) => skill.tags?.includes(RESOURCE_TAG_FAVORITE))
    .map((skill) => (
      <SkillsRailDraggableSkillRow
        key={skill.id}
        skill={skill}
        dragNamespace="favorites"
        section="favorites"
        active={activeSkillId === skill.id}
      />
    ));

  const skillRows = skills.map((skill) => (
    <SkillsRailDraggableSkillRow
      key={skill.id}
      skill={skill}
      dragNamespace="all"
      active={activeSkillId === skill.id}
    />
  ));

  const visibleTemplates = templates.filter(
    (template) =>
      !template.already_copied ||
      (!!me?.user.id && template.published_by_user_id === me.user.id),
  );

  const templateRows = visibleTemplates.map((template) => (
    <TemplatesRailRow
      key={template.id}
      template={template}
      active={activeTemplateId === template.id}
    />
  ));

  return (
    <RailMultiSelectionMenuProvider actions={leftRailMultiActions}>
      {/* Positioning context for the floating selection bar, which overlays
          the top of the scroll area instead of pushing the rail down. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <RailSelectionBar
          count={selectionCount}
          registryActions={selectionBarActions}
          onClose={clear}
        />
        <div
          className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pb-2 pt-3"
          {...{ [RAIL_SELECTION_ROOT_ATTR]: "" }}
          onClick={clearIfOutside}
        >
          <Accordion
            type="multiple"
            defaultValue={["skills-mine"]}
            className="flex flex-col gap-2"
          >
            <RailFavoritesDropTarget resourceType="skill">
              <RailGroup
                groupId="skills-favorites"
                label="Favorites"
                icon={Star}
                noCap
                items={favoriteRows}
                emptyState={
                  <RailZeroState
                    icon={Star}
                    variant="passive"
                    title="Pin what matters."
                    description="Add a skill to favorites and it stays here."
                  />
                }
              />
            </RailFavoritesDropTarget>
            <RailGroup
              groupId="skills-mine"
              label="My Skills"
              icon={User}
              noCap
              onContextMenu={addSkillMenu.onContextMenu}
              items={[
                // The list's own closing verb rides INSIDE the section so it
                // collapses with it (Aug 2026 ruling). It is the section's
                // one create control — no head plus — so it stays under the
                // quiet zero-state line when the list is empty, and it
                // offers the same create-or-upload choice as the head glyph.
                ...(skillRows.length > 0
                  ? skillRows
                  : [
                      <RailZeroState
                        key="skills-empty"
                        icon={ScrollText}
                        accent="sage"
                        title={railZeroStateCopy.skills.mine.title}
                        description={railZeroStateCopy.skills.mine.description}
                      />,
                    ]),
                <SkillsRailAddSkillMenu
                  key="new-skill"
                  createFromScratch={createFromScratch}
                  openUploadZipDialog={openUploadZipDialog}
                  // The trigger is the whole row, so a plain `start` would
                  // pin the menu to the row's left edge, left of the ＋. Line
                  // its leading edge up with the label text instead: the
                  // row's px-2 (8) + icon slot size-4 (16) + gap-2.5 (10).
                  alignOffset={34}
                >
                  <ActionRow icon={Plus} label="New skill" align="row" />
                </SkillsRailAddSkillMenu>,
              ]}
            />

            <RailGroup
              groupId="templates"
              label="Templates"
              icon={LayoutTemplate}
              items={templateRows}
              emptyState={
                <RailZeroState
                  icon={LayoutTemplate}
                  variant="passive"
                  title="No templates yet."
                  description="Copy a starter template or publish a skill to share one."
                />
              }
            />
          </Accordion>
        </div>
      </div>

      <RailRowMenu
        {...railRowMenuProps(addSkillMenu)}
        kebab={false}
        label="My Skills"
      >
        {addSkillMenuItems(createFromScratch, openUploadZipDialog)}
      </RailRowMenu>
      {uploadZipDialog}
    </RailMultiSelectionMenuProvider>
  );
}
